import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Gift, Twitter, Facebook, Linkedin, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

export const usePrizeBadges = (enabled = true) => {
  const [data, setData] = useState(null);
  useEffect(() => { if (enabled) api.get("/me/prize-badges").then(({ data }) => setData(data)).catch(() => {}); }, [enabled]);
  return data;
};

const BadgeShare = ({ b }) => {
  const url = `${window.location.origin}/ear-trainer`;
  const text = `I finished ${b.week} at #1 on The JT Hub ${b.board} leaderboard (${b.level})! Can you beat me?`;
  const open = (u) => window.open(u, "_blank", "noopener");
  const links = [
    ["x", Twitter, `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`],
    ["facebook", Facebook, `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`],
    ["linkedin", Linkedin, `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`],
    ["whatsapp", MessageCircle, `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`],
  ];
  return (
    <div className="mt-2 flex justify-center gap-1.5">
      {links.map(([id, Icon, href]) => (
        <button key={id} onClick={() => open(href)} aria-label={`Share on ${id}`} className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-primary/15 hover:text-primary" data-testid={`prize-share-${id}-${b.week}-${b.level}`}>
          <Icon size={13} />
        </button>
      ))}
    </div>
  );
};

export const PrizeBadges = ({ data }) => {
  if (!data) return null;
  const toward = data.count % data.needed;
  return (
    <div className="mt-10" data-testid="prize-badges">
      <div className="flex items-center gap-2">
        <Crown size={18} className="text-primary" />
        <h2 className="font-serif text-2xl">Leaderboard prize badges</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Finish a week at #1 on any weekly leaderboard (Melody Ear Trainer or Sight-Reading Trainer) to win a badge. Every 10 badges earns a half-price 1:1 session with Jason.</p>
      <div className="mt-4 rounded-2xl border border-border bg-card p-5">
        <div className="flex items-center justify-between text-sm">
          <span data-testid="prize-badge-count"><span className="font-serif text-2xl">{data.count}</span> badge{data.count === 1 ? "" : "s"} earned</span>
          <span className="text-muted-foreground" data-testid="prize-badge-progress">{toward}/{data.needed} towards your next half-price session</span>
        </div>
        <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary"><div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(toward / data.needed) * 100}%` }} /></div>
        {data.half_price_available > 0 && (
          <Link to="/pricing#one-to-one" className="mt-4 flex items-center gap-2 rounded-xl bg-primary/15 p-3 text-sm text-primary" data-testid="prize-half-price-ready">
            <Gift size={16} /> You have {data.half_price_available} half-price 1:1 session{data.half_price_available > 1 ? "s" : ""} ready. Book now →
          </Link>
        )}
        {data.badges.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {data.badges.map((b) => (
              <div key={`${b.board}-${b.week}-${b.level}`} className="rounded-xl border border-primary/40 bg-primary/5 p-3 text-center" data-testid={`prize-badge-${b.week}-${b.level}`}>
                <Crown className="mx-auto text-primary" size={22} />
                <p className="mt-1 text-xs font-medium">Weekly #1</p>
                <p className="text-[11px] text-muted-foreground">{b.board} · {b.level}</p>
                <p className="font-mono text-[10px] text-muted-foreground">{b.week}</p>
                <BadgeShare b={b} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
